import { Touchable } from "@kalyzee/kast-web-sdk/web-components";
import { WebRTCSession, WebrtcSessionStatsSubscription } from "@kalyzee/kast-web-sdk/webrtc";
import React, { ForwardedRef, useEffect, useRef, useState } from "react";
import { WebrtcSessionStats } from "./WebrtcSessionStats";
import IconChevron from "../../assets/icons/chevron.svg?react";
import { MeetingSession, MeetingSessionMedia } from "@kalyzee/kast-web-sdk/websocket";
import { toastError, toastSuccess } from "../../helpers/toast";

export interface BottomPanelWebrtcSessionStatsProps {
  incomingSessions?: WebRTCSession<any>[];
  outgoingSessions?: WebRTCSession<any>[];
  sessions: { id: string; name: string; session?: WebRTCSession<any>; meeting?: { session: MeetingSession; mediaSession: MeetingSessionMedia } }[];
  position?: "left" | "right" | "bottom" | "top";
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export interface BottomPanelWebrtcSessionStatsRef {}

export const BottomPanelWebrtcSessionStats = React.forwardRef(
  (
    { incomingSessions, outgoingSessions, position = "top", sessions, children, className, style }: BottomPanelWebrtcSessionStatsProps,
    forwardRef: ForwardedRef<BottomPanelWebrtcSessionStatsRef | undefined>
  ) => {
    const [state, setState] = useState<"closed" | "opened">("closed");
    const vertical = position === "left" || position === "right";
    const bitratesDownRef = useRef<{ [id: string]: number }>({});
    const bitratesUpRef = useRef<{ [id: string]: number }>({});
    const [totalBitrateDown, setTotalBitrateDown] = useState(0);
    const [totalBitrateUp, setTotalBitrateUp] = useState(0);

    useEffect(() => {
      const interval = setInterval(() => {
        let total = 0;
        Object.keys(bitratesUpRef.current).forEach((id) => total += bitratesUpRef.current[id]);
        setTotalBitrateUp(total);
        total = 0;
        Object.keys(bitratesDownRef.current).forEach((id) => total += bitratesDownRef.current[id]);
        setTotalBitrateDown(total);
      }, 500);
      return () => {
        clearInterval(interval);
      }
    }, []);

    useEffect(() => {
      const subscriptions: WebrtcSessionStatsSubscription<any, any, any>[] = [];
      incomingSessions?.forEach((s) => {
        subscriptions.push(s.subscribeStats(1000, (_, mode, stream, stats) => {
          const bitrate = (stats.video?.stats.bitrate) ?? 0 + (stats.audio?.stats.bitrate ?? 0);
          bitratesDownRef.current[s.getId()] = bitrate;
        }))
      });
      const ids = Object.keys(bitratesDownRef.current);
      ids.forEach((id) => {
        if (!incomingSessions?.find((s) => s.getId() === id)) {
          delete bitratesDownRef.current[id];
        }
      });
      return () => {
        subscriptions.forEach((s) => s.destroy());
      }
    }, [incomingSessions]);

    useEffect(() => {
      const subscriptions: WebrtcSessionStatsSubscription<any, any, any>[] = [];
      outgoingSessions?.forEach((s) => {
        subscriptions.push(s.subscribeStats(1000, (_, mode, stream, stats) => {
          const bitrate = (stats.video?.stats.bitrate) ?? 0 + (stats.audio?.stats.bitrate ?? 0);
          bitratesUpRef.current[s.getId()] = bitrate;
        }))
      });
      const ids = Object.keys(bitratesUpRef.current);
      ids.forEach((id) => {
        if (!outgoingSessions?.find((s) => s.getId() === id)) {
          delete bitratesUpRef.current[id];
        }
      });
      return () => {
        subscriptions.forEach((s) => s.destroy());
      }
    }, [outgoingSessions]);

    const renderBitrates = () => {
      if (!incomingSessions && !outgoingSessions) return null;
      const data = `${incomingSessions ? `DOWN: ${(totalBitrateDown / (1024 * 1024)).toFixed(2)}Mbits      ` : ''}${outgoingSessions ? `UP: ${(totalBitrateUp / (1024 * 1024)).toFixed(2)}Mbits ` : ''}`;
      return (
          <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            justifyContent: "center",
            alignItems: "center",
            gap: "5px",
            textAlign: 'center',
            width: '100',
            color: 'white',
            fontWeight: 'bold',
            fontSize: '1.5rem'
          }}
        >
          {data}
        </div>
      )
    };

    const renderContent = () => (
      <div
        style={{
          ...(vertical
            ? {
                flexDirection: "column",
                height: "100%",
              }
            : {
                flexDirection: "row",
                width: "100%",
              }),
          justifyContent: "flex-start",
          alignItems: "flex-start",
          flexWrap: "wrap",
          gap: "5px",
          display: 'flex'
        }}
      >
        {sessions?.map((s) => {
          const meeting = s.meeting;
          const sessionIds = meeting
            ? `User: ${s.meeting?.session?.user?.id}\nSession: ${s.meeting?.session?.id}\nMedia: ${s?.meeting?.mediaSession?.id}`
            : undefined;

          return (
            <>
              <div
                key={`bottom_panel_webrtc_session_stats_${s.id}`}
                style={{
                  ...(vertical ? { minHeight: "200px" } : { minWidth: "400px" }),
                  flex: 1,
                  color: "white",
                  background: "#222222BB",
                  padding: "5px",
                  borderRadius: "5px",
                  fontSize: "12px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div style={{ width: "100%", fontSize: "16px", fontWeight: "bold", textAlign: "center" }}>{s.name}</div>
                <WebrtcSessionStats session={s.session} style={{ flex: 1 }}>
                  {sessionIds && (
                    <div
                      style={{
                        width: "100%",
                        color: "white",
                        background: "#00000088",
                        cursor: "pointer",
                        padding: "5px",
                        fontSize: "10px",
                        whiteSpace: "pre",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        borderRadius: "5px",
                      }}
                      onClick={async (e) => {
                        e.stopPropagation();
                        try {
                          await navigator.clipboard.writeText(sessionIds);
                          toastSuccess(`Copié dans le presse-papier...`);
                        } catch (err) {
                          toastError(`Impossible de copier les données dans le presse-papier: ${err}`);
                        }
                      }}
                    >
                      {sessionIds}
                    </div>
                  )}
                </WebrtcSessionStats>
              </div>
            </>
          );
        })}
      </div>
    );

    const currStyle: React.CSSProperties = {
      ...(style ?? {}),
      position: "absolute",
      zIndex: 10000,
      padding: "5px",
      display: "flex",
      flexDirection: vertical ? "row" : "column",
      justifyContent: "flex-start",
      alignItems: "flex-start",
      gap: "5px",
      backgroundColor: "#000000BB",
    };

    let chevronTransform = "rotate(0deg)";
    if (position === "left") {
      chevronTransform = state === "closed" ? "rotate(90deg)" : "rotate(-90deg)";
      currStyle.top = 0;
      currStyle.bottom = 0;
      currStyle.left = 0;
    } else if (position === "right") {
      chevronTransform = state === "closed" ? "rotate(-90deg)" : "rotate(90deg)";
      currStyle.top = 0;
      currStyle.bottom = 0;
      currStyle.right = 0;
    } else if (position === "top") {
      chevronTransform = state === "closed" ? "rotate(180deg)" : "rotate(0deg)";
      currStyle.top = 0;
      currStyle.left = 0;
      currStyle.right = 0;
    } else {
      chevronTransform = state === "closed" ? "rotate(0deg)" : "rotate(180deg)";
      currStyle.bottom = 0;
      currStyle.left = 0;
      currStyle.right = 0;
    }
    return (
      <div className={className} style={currStyle}>
        <Touchable
          style={{
            ...(vertical ? { height: "100%" } : { width: "100%" }),
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "2px",
            textAlign: "center",
            color: "white",
            fontSize: "24px",
          }}
          onPress={() => {
            setState(state === "closed" ? "opened" : "closed");
          }}
        >
          <IconChevron width={20} height={20} style={{ transform: chevronTransform }} />
        </Touchable>
        <div
          style={{
            ...(vertical ? { height: "100%" } : { width: "100%" }),
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: "10px",
            display: state === "opened" ? "flex" : "none",
          }}
        >
          {renderBitrates()}
          {renderContent()}
          {children}
        </div>
      </div>
    );
  }
);
